import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  SystemEntity,
  ReferenceType,
  type ReferenceEntityType,
} from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useExternalSystemReferencesByEntities } from '@/api/rpc/hooks/core/useExternalSystemReferences';
import { SystemEntityInline } from './inline-ui';

interface ExternalReferencesListProps {
  /** Entities whose ESRs are considered "linked" (associated documents). */
  entities: { entityType: ReferenceEntityType; entityId: string }[];
  /** Entities whose ESRs are "non-linked" (e.g. invoice itself). Hidden by default, shown via toggle. */
  nonLinkedEntities?: { entityType: ReferenceEntityType; entityId: string }[];
  className?: string;
  /** Max references to show before collapsing. Defaults to 3. */
  collapsedLimit?: number;
  /** Rendered when there are no external references. */
  fallback?: React.ReactNode;
}

const REFERENCE_TYPE_ORDER: ReferenceType[] = [
  ReferenceType.PurchaseOrder,
  ReferenceType.SalesOrder,
  ReferenceType.TransferOrder,
  ReferenceType.Invoice,
  ReferenceType.BillOfLading,
  ReferenceType.Shipment,
  ReferenceType.ProofOfDelivery,
  ReferenceType.Other,
];

function sortByReferenceType<
  T extends { referenceType: ReferenceType; referenceValue: string },
>(refs: T[]): T[] {
  return [...refs].sort((a, b) => {
    const ai = REFERENCE_TYPE_ORDER.indexOf(a.referenceType);
    const bi = REFERENCE_TYPE_ORDER.indexOf(b.referenceType);
    if (ai !== bi) return ai - bi;
    return a.referenceValue.localeCompare(b.referenceValue);
  });
}

export function ExternalReferencesList({
  entities,
  nonLinkedEntities,
  className,
  collapsedLimit = 3,
  fallback,
}: ExternalReferencesListProps) {
  const gt = useGT();
  const { references: linkedRefs, isLoading: linkedLoading } =
    useExternalSystemReferencesByEntities(entities);
  const { references: nonLinkedRefs, isLoading: nonLinkedLoading } =
    useExternalSystemReferencesByEntities(nonLinkedEntities ?? []);

  const [expanded, setExpanded] = useState(false);
  const [showNonLinked, setShowNonLinked] = useState(false);

  const isLoading = linkedLoading || nonLinkedLoading;

  if (isLoading) {
    return (
      <p className={cn('text-xs text-muted-foreground', className)}>
        <T>Loading references...</T>
      </p>
    );
  }

  // Deduplicate non-linked refs that already appear in linked set
  const linkedIds = new Set(linkedRefs.map((r) => r.id));
  const uniqueNonLinked = nonLinkedRefs.filter((r) => !linkedIds.has(r.id));

  const visibleRefs = showNonLinked
    ? sortByReferenceType([...linkedRefs, ...uniqueNonLinked])
    : sortByReferenceType(linkedRefs);

  const totalCount = visibleRefs.length;

  if (totalCount === 0 && uniqueNonLinked.length === 0) {
    return fallback ?? null;
  }

  const showCollapse = totalCount > collapsedLimit;
  const displayRefs = expanded
    ? visibleRefs
    : visibleRefs.slice(0, collapsedLimit);

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex flex-wrap gap-1.5">
        {displayRefs.map((ref) => (
          <SystemEntityInline
            key={ref.id}
            entityType={SystemEntity.EXTERNAL_SYSTEM_REFERENCE}
            entityId={ref.id}
            className="text-xs"
          />
        ))}
      </div>

      <div className="flex items-center gap-2">
        {showCollapse && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded
              ? gt('Show less')
              : gt('+{count} more', {
                  count: String(totalCount - collapsedLimit),
                  context: 'expand references button',
                })}
          </button>
        )}
        {uniqueNonLinked.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setExpanded(false);
              setShowNonLinked(!showNonLinked);
            }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showNonLinked
              ? gt('Hide non-linked')
              : gt('Show {count} non-linked', {
                  count: String(uniqueNonLinked.length),
                  context: 'show non-linked references button',
                })}
          </button>
        )}
      </div>
    </div>
  );
}
